import { CloseOutlined, LanguageOutlined } from '@mui/icons-material'
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    Icon,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Typography,
} from '@mui/material'
import MapSpace from './MapSpace'
import { ReactElement, useCallback, useState } from 'react'
import TruncatedText from './TruncatedText'
import { useTranslation } from 'react-i18next'

interface MapInputProps {
    id: string
    label: string
    value: GeoJSON.FeatureCollection
    onChange: any
    required: boolean
    disabled?: boolean
    onClickMap?: (val: boolean) => void
    errorMessage?: string
}

const MapInput = (props: MapInputProps): ReactElement => {
    const {
        id,
        label,
        value,
        onChange,
        required,
        disabled,
        onClickMap,
        errorMessage = '',
    } = props
    const { t } = useTranslation()

    const [isMapOpen, setIsMapOpen] = useState(false)
    const address = value?.features?.[0]?.properties?.address || ''

    const onClickMapIcon = useCallback(() => {
        setIsMapOpen(true)
        onClickMap?.(true)
    }, [onClickMap])

    const onCancelInMap = useCallback(() => {
        setIsMapOpen(false)
        onClickMap?.(false)
    }, [onClickMap])

    const onAddLocation = useCallback(
        (value: GeoJSON.FeatureCollection) => {
            onChange(id, value)
            onCancelInMap()
        },
        [id, onChange, onCancelInMap],
    )

    const onClearLocation = useCallback(() => {
        onChange(id, '')
    }, [id, onChange])

    const CustomInputComponent: React.FC<any> = (props) => {
        const { value, ...rest } = props
        return (
            <div {...rest}>
                {/* Render the truncated text */}
                <TruncatedText
                    text={value || ''}
                    sx={{ fontSize: 14, color: '#000' }}
                />
            </div>
        )
    }

    return (
        <>
            <Box
                sx={{
                    width: '100%',
                }}
            >
                <FormControl fullWidth disabled={!!disabled}>
                    <Typography sx={{ opacity: '0.7', fontSize: '14px' }}>
                        {label}
                        {required ? '*' : ''}
                    </Typography>
                    <OutlinedInput
                        size="small"
                        id={id}
                        label=""
                        value={address}
                        // onChange={(e) => onChangeField(id, e.target.value)}
                        inputComponent={
                            address ? CustomInputComponent : undefined
                        } // Use a div as the base input
                        sx={{
                            // width: '100%',
                            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                                {
                                    display: 'none',
                                },
                            '& input[type=number]': {
                                MozAppearance: 'textfield',
                            },
                            '& fieldset': {
                                borderColor: `${errorMessage ? 'red !important' : ''}`,
                            },
                            paddingRight: '2px',
                            '&:hover': {
                                '& .close-icon': {
                                    display: 'flex',
                                },
                            },
                        }}
                        placeholder={t('pickLabel', { label })}
                        inputProps={{
                            style: {
                                fontSize: 14,
                                WebkitTextFillColor:
                                    address !== '' &&
                                    address !== null &&
                                    !disabled
                                        ? '#000'
                                        : '#00000061',
                            },
                            // children: <TruncatedText text={val} />,
                            'aria-label': t('pickLabel', { label }),
                        }}
                        endAdornment={
                            <InputAdornment position="end">
                                {!disabled && value && (
                                    <IconButton
                                        className="close-icon"
                                        sx={{
                                            display: 'none',
                                        }}
                                        id={`${id}-clear-button`}
                                        onClick={
                                            disabled
                                                ? undefined
                                                : onClearLocation
                                        }
                                        disabled={!!disabled}
                                        aria-label={t('clearLocation')}
                                        title={t('clearLocation')}
                                    >
                                        <CloseOutlined fontSize="small" />
                                    </IconButton>
                                )}
                                <IconButton
                                    id={`${id}-map-button`}
                                    onClick={onClickMapIcon}
                                    aria-label={t('openMap')}
                                    title={t('openMap')}
                                >
                                    <LanguageOutlined />
                                </IconButton>
                            </InputAdornment>
                        }
                        disabled
                        error={errorMessage !== ''}
                        aria-labelledby={`${id}-input-label`}
                    />
                    <span id={`${id}-input-label`} style={{ display: 'none' }}>
                        {label}
                    </span>
                    {errorMessage !== '' && (
                        <FormHelperText sx={{ color: 'red' }}>
                            {errorMessage}
                        </FormHelperText>
                    )}
                </FormControl>
            </Box>
            {isMapOpen && (
                <Dialog
                    open={true}
                    scroll="paper"
                    PaperProps={{
                        sx: {
                            width: '100%',
                            maxWidth: '1000px!important',
                        },
                    }}
                    aria-labelledby="scroll-dialog-title"
                    aria-describedby="scroll-dialog-description"
                    sx={{ padding: '10px 30px' }}
                >
                    <Box>
                        <DialogTitle id="scroll-dialog-title">
                            <Box
                                sx={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '15px',
                                    cursor: 'pointer',
                                }}
                            >
                                <Icon
                                    onClick={onCancelInMap}
                                    sx={{ marginRight: '8px' }}
                                    fontSize="small"
                                    id={`add-location-cancel-button`}
                                    aria-label={t('closeDialog')}
                                    title={t('closeDialog')}
                                >
                                    <CloseOutlined
                                        sx={{
                                            color: '#000',
                                            marginTop: '-2px',
                                        }}
                                    />
                                </Icon>
                            </Box>
                            <Box
                                sx={{ marginBottom: '5px' }}
                                display="flex"
                                alignItems="center"
                            >
                                <Typography
                                    sx={{
                                        color: '#000',
                                        fontSize: '18px',
                                        fontWeight: 600,
                                    }}
                                    id="scroll-dialog-description"
                                >
                                    {t('addNewLocation')}
                                </Typography>
                            </Box>
                        </DialogTitle>
                        <DialogContent
                            sx={{
                                height: 'calc(100vh - 250px)',
                            }}
                        >
                            <Box sx={{ height: '100%' }}>
                                <MapSpace
                                    onAddLocation={onAddLocation}
                                    value={value}
                                    disableEdit={disabled || false}
                                />
                            </Box>
                        </DialogContent>
                    </Box>
                </Dialog>
            )}
        </>
    )
}

export default MapInput
